<template>
  <div class="relative">
    <div class="template-info w-[1280px] mx-auto pb-[65px] relative">
      <a-breadcrumb class="mt-[30px] mb-[20px]" :separator="separator">
        <a-breadcrumb-item>
          <nuxt-link to="/template">{{ $t('mo-ban-zhong-xin') }}</nuxt-link>
        </a-breadcrumb-item>
        <a-breadcrumb-item>{{ calData?.name }}</a-breadcrumb-item>
      </a-breadcrumb>
      <div
        class="w-full h-[720px] rounded-[10px] bg-[#2B2B2E] mr-[20px] overflow-hidden"
      >
        <VideoPlayer
          v-if="calData?.preview_m3u8_url || calData?.full_video_url"
          :id="templateId"
          :url="calData?.full_video_url"
          :auto-url="calData?.preview_m3u8_url"
          :autoplay="true"
          :muted="true"
          :poster="calData?.cover_url"
          type="template"
          fetchpriority="high"
        />
      </div>
      <div class="template-title flex-y-center mt-[24px]">
        <h1 class="text-[24px] font-semibold">{{ calData?.name }}</h1>
        <iconpark-icon
          v-if="isHot && !calData?.is_new"
          name="fire"
          size="18"
          class="text-error-6 ml-[3px]"
        ></iconpark-icon>
        <span
          v-if="calData?.is_new"
          class="w-[44px] h-[20px] ml-[3px] flex-y-center bg-primary rounded-[6px] justify-center"
        >
          <iconpark-icon
            name="new"
            class="text-white"
            size="30"
          ></iconpark-icon>
        </span>
      </div>
      <div
        class="template-desc mt-[8px] flex-y-center gap-[10px] text-font-40 text-12"
      >
        <span
          class="flex-y-center gap-[4px]"
          :class="{
            'cursor-pointer hover:text-font-70 ': true,
          }"
          :title="avatarName"
          @click.stop="handAvatarClick"
        >
          <img loading="lazy" class="avatar" :src="avatarUrl" />
          {{ avatarName }}
        </span>
      </div>

      <!-- 描述 -->
      <ToggleText
        :class="[
          'text-14 text-font-70 leading-[28px] mt-[20px] mb-[16px] relative',
        ]"
        :message="calData?.message || ''"
      ></ToggleText>

      <!-- 标签 -->
      <TagList :new_tag_ids="calData?.new_tag_ids" />

      <!-- 脚本文本 -->
      <SegmentText
        v-if="calData?.segment_text"
        :text="calData?.segment_text"
      ></SegmentText>

      <div class="my-[20px] w-full h-[2px] bg-back-2"></div>
      <div class="flex-y-center gap-[20px] mb-[40px]">
        <a-button
          type="primary"
          class="w-[390px] h-[32px]"
          @click="useTemplate(calData)"
        >
          {{
            calData?.copyright_limited
              ? $t('ban-quan-xiang-guan-zhi-du-mo-ban')
              : $t('chuang-zuo-tong-kuan')
          }}
        </a-button>
        <div class="flex-center gap-[12px]">
          <Collect
            v-if="calData"
            :modelValue="calData?.is_star_personal"
            :params="{
              model_id: calData?.id || -1,
              model_type: COLLECTION_MAP.PIECES_TEMPLATE,
              source: COLLECTION_SOURCE.YOUYAN,
            }"
            :data="calData"
            @change="onCollectChange"
          />
          <TemplateShare
            :isCard="false"
            :trackParams="{
              target: TARGET_FROM.Template,
              dataId: calData?.id,
              dataTitle: calData?.title,
            }"
            :params="{
              share_ids: [templateId],
              model_type: COLLECTION_MAP.PIECES_TEMPLATE,
            }"
            :templateId="templateId"
            :linkUrl="`${location?.origin}/template/${templateData?.id}.html`"
          />
        </div>
      </div>
      <Similar
        :title="$t('wei-wo-tui-jian')"
        :active="calData"
        :cardHeight="255"
        @similarClick="onSimilarClick"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { isEmpty } from 'lodash-es'
import SegmentText from './segment-text.vue'
import Collect from '@/features/collect/index.vue'
import { COLLECTION_MAP } from '~/constants/dict'
import { getTemplate } from '~/api'
import { isChinese } from '~/utils/area'
import { TARGET_FROM } from '~/utils/track'
import { COLLECTION_SOURCE } from '~/utils/constant'
import TagList from '~/features/template/components/tag-list.vue'
import ToggleText from '~/components/toggle-text/index.vue'
import { TemplateShare } from '~/features/template/components/templateShare'
import { Similar } from '~/features/template/components/similar.tsx'
import { useTemplate } from '~/features/template/components/info'
import type { Template } from '~/api/interface'
import { useWatchPageSeoData } from '~/hooks/useSeo'
import { PageKey } from '~/constants/page'
import { useStartPatch } from '~/hooks/useStarPatch'

const { $t } = useNuxtApp()
const route = useRoute()

const separator = h('iconpark-icon', { name: 'breadcrumb-separator', size: 10 })

const templateId = computed(() => Number(String(route.params.id).split('-')[0]))

function autoSuffix(t: string) {
  const suffix = isChinese() ? `-${$t('you-yan')}AI` : `-${$t('you-yan')}`
  if (t.endsWith(suffix)) {
    return t
  }

  return t + suffix
}

function getSeoDescriptionFromScript(text: string) {
  const str = text.split('\n')[0] || ''
  return str.slice(0, isChinese() ? 30 : 60)
}

const { data: templateData } = useAsyncData(async () => {
  const { data } = await getTemplate({
    template_id: templateId.value,
    query_star: import.meta.client,
  })

  if (!isEmpty(data)) {
    data.seo_title = data.seo_title || autoSuffix(data.name)
    data.seo_description =
      data.seo_description ||
      data.message ||
      getSeoDescriptionFromScript(data.segment_text)
    data.query_star = import.meta.client
  }

  return data as Template
})

watch(
  () => templateData.value,
  (data) => {
    if (data && !data?.id) {
      throw new Error(`no template found for id: ${templateId.value}`)
    }
  },
  { immediate: true, flush: 'sync' }
)

const { calData } = useStartPatch(templateData, 3)

useWatchPageSeoData(templateData)

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: templateData.value?.seo_title,
  description: templateData.value?.seo_description,
  contentUrl:
    templateData.value?.preview_m3u8_url || templateData.value?.full_video_url,
  uploadDate: templateData.value?.create_time,
  thumbnailUrl: templateData.value?.cover_url,
}))

function changeUrl() {
  if (!templateData.value) {
    return
  }

  const memoizedPath = useMemoizedPath()
  if (memoizedPath.value === null) {
    memoizedPath.value = new URL(window.location.href).pathname
  }

  const { id } = templateData.value
  memoizedPath.value = new URL(window.location.href).pathname
  const link = `/template/${id}.html`
  history.replaceState({}, '', link)
}

watch(() => templateData.value, changeUrl)

const avatarUrl = computed(() =>
  !templateData.value
    ? ''
    : transformImage(
        templateData.value?.avatar_url || templateData.value?.user_logo_url,
        { height: 36 }
      )
)

const avatarName = computed(() =>
  !templateData.value
    ? ''
    : templateData.value?.name_for_show || templateData.value?.username
)

// 是否热门
const isHot = computed(() => {
  if (!templateData.value || !templateData.value.tags) {
    return false
  }

  return templateData.value?.tags?.includes($t('re-men'))
})

const location = computed(() => {
  if (import.meta.client) {
    return window.location
  }

  return null
})

const handAvatarClick = () => {
  if (templateData.value) {
    const router = useRouter()
    router.push({
      name: PageKey.PERSONAL_PAGE,
      params: {
        key: templateData.value.homepage_org_id || 0,
      },
    })
  }
}

const onCollectChange = (val: boolean) => {
  if (templateData.value) {
    templateData.value.is_star_personal = val
  }
}

const onSimilarClick = (_: Template[], data: Template) => {
  const router = useRouter()
  router.push({
    name: PageKey.TEMPLATE_INFO,
    params: { id: data.id },
  })
}

provideResourceLocation({
  source: SOURCE.TEMPLATE_CENTER,
})

watch(
  () => templateData.value,
  (val) => {
    if (val && import.meta.client) {
      trackEvent(TRACK_EVENT.PLAY_TEMPLATE_VIDEO, {
        source: val.source,
        template_id: val.id,
        template_title: val.title,
      })
    }
  }
)
</script>

<style scoped lang="scss">
.avatar {
  @apply w-[16px] h-[16px] rounded-[50%];
  border: 1px solid var(--back-3);
}
.line {
  width: 1px;
  height: 10px;
  background: var(--text-1);
  opacity: 0.2;
}

.message {
  position: relative;
  cursor: pointer;
  &.hide {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.toggle {
  position: absolute;
  cursor: pointer;
  right: 0;
  bottom: -28px;
  color: var(--primary-color);
}
</style>
